import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CampaignDto, CampaignStatus, TerritoryDto } from '@rw/shared';
import { CAMPAIGN_MAX_CELLS } from '@rw/shared';
import { useMemo, useState } from 'react';
import { Sheet } from '../../components/Sheet';
import { LoadingState } from '../../components/states';
import { Button, Card } from '../../components/ui';
import { useLoadedRegion, useRegions } from '../../hooks/queries';
import { ApiError, api } from '../../lib/api';
import { formatDate, formatNumber } from '../../lib/format';
import { fittedLogo } from '../../lib/resize-image';
import { t as tr, useT } from '../../i18n';
import { toast } from '../../stores/toasts';
import { campaignKeys, useCampaigns } from '../campaigns/campaigns-api';
import { GameMap } from '../map/GameMap';
import { BrandLogo } from './BrandsTab';
import { useBrands } from './brands-api';

const input =
  'mt-1 h-11 w-full rounded-xl border border-ink-700 bg-ink-850 px-3 outline-none focus:border-volt';
const STATUS: Record<CampaignStatus, { label: string; cls: string }> = {
  ACTIVE: { label: 'Live', cls: 'bg-volt/15 text-volt' },
  SCHEDULED: { label: 'Scheduled', cls: 'bg-sky/15 text-sky' },
  PAUSED: { label: 'Paused', cls: 'bg-gold/15 text-gold' },
  ENDED: { label: 'Ended', cls: 'bg-ink-700 text-ink-300' },
};
const NO_OWNERS = new Map<string, TerritoryDto>();

/** `<input type="datetime-local">` value in the device's time zone. */
const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

/** Game owner: brand campaigns — sponsored map cells (bonus XP) and a Home banner, per city. */
export function CampaignsTab() {
  const q = useCampaigns();
  const regions = useRegions();
  const t = useT();
  const [editing, setEditing] = useState<CampaignDto | 'new' | null>(null);
  const [picking, setPicking] = useState<CampaignDto | null>(null);
  if (!q.data) return <LoadingState />;
  const cityName = (id: string) => regions.data?.find((r) => r.id === id)?.name ?? id;
  return (
    <div className="mt-4 space-y-3" data-testid="admin-campaigns">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-ink-400">
          {t('Sponsored map cells and Home banners. Runners get bonus XP the first time they capture a sponsored cell.')}
        </p>
        <Button size="sm" className="shrink-0 whitespace-nowrap" onClick={() => setEditing('new')} data-testid="campaign-new">
          {t('+ New')}
        </Button>
      </div>
      {q.data.length === 0 ? <p className="text-sm text-ink-500">{t('No campaigns yet.')}</p> : null}
      {q.data.map((c) => (
        <div key={c.id} data-testid="campaign-row">
          <Card className="space-y-2">
            <div className="flex items-center gap-3">
              <BrandLogo brand={c.brand} className="h-10 w-16" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold" data-testid="campaign-row-name">
                    {c.name}
                  </span>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS[c.status].cls}`}
                  >
                    {t(STATUS[c.status].label)}
                  </span>
                </div>
                <div className="truncate text-xs text-ink-400">
                  {c.brand.name} · {cityName(c.regionId)} · {formatDate(c.startsAt)} –{' '}
                  {formatDate(c.endsAt)}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1 text-center text-[11px] text-ink-400">
              <Mini label={t('cells')} value={c.cells.length} />
              <Mini
                label={t('cell views / taps')}
                value={`${formatNumber(c.stats.cellViews)} / ${formatNumber(c.stats.cellClicks)}`}
              />
              <Mini
                label={t('banner views / taps')}
                value={
                  c.bannerUrl
                    ? `${formatNumber(c.stats.bannerViews)} / ${formatNumber(c.stats.bannerClicks)}`
                    : '—'
                }
              />
              <Mini label={t('runners bonused')} value={c.stats.bonusRunners} />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="flex-1"
                onClick={() => setEditing(c)}
                data-testid={`campaign-edit-${c.name}`}
              >
                {t('Edit')}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="flex-1"
                onClick={() => setPicking(c)}
                disabled={c.status === 'ENDED'}
                data-testid={`campaign-cells-${c.name}`}
              >
                {t('Map cells')}
              </Button>
            </div>
          </Card>
        </div>
      ))}
      <CampaignSheet
        campaign={editing}
        onClose={() => setEditing(null)}
        onCreated={(c) => setPicking(c)}
      />
      {picking ? (
        <CellPicker campaign={picking} all={q.data} onClose={() => setPicking(null)} />
      ) : null}
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-ink-850 px-1 py-1.5">
      <div className="font-semibold text-ink-100 tabular">{value}</div>
      {label}
    </div>
  );
}

function CampaignSheet({
  campaign,
  onClose,
  onCreated,
}: {
  campaign: CampaignDto | 'new' | null;
  onClose: () => void;
  onCreated: (c: CampaignDto) => void;
}) {
  const qc = useQueryClient();
  const t = useT();
  const brands = useBrands(!!campaign);
  const regions = useRegions();
  const existing = campaign && campaign !== 'new' ? campaign : null;
  const [form, setForm] = useState<Record<string, string>>({});
  const [paused, setPaused] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [removeBanner, setRemoveBanner] = useState(false);
  const key = existing?.id ?? 'new';
  const [formKey, setFormKey] = useState<string | null>(null);
  if (campaign && formKey !== key) {
    const now = Date.now();
    setFormKey(key);
    setForm({
      brandId: existing?.brand.id ?? '',
      name: existing?.name ?? '',
      regionId: existing?.regionId ?? 'DA_NANG',
      startsAt: toLocalInput(existing?.startsAt ?? new Date(now).toISOString()),
      endsAt: toLocalInput(existing?.endsAt ?? new Date(now + 30 * 86_400_000).toISOString()),
      link: existing?.link ?? '',
    });
    setPaused(existing?.paused ?? false);
    setBanner(null);
    setRemoveBanner(false);
  }
  const close = () => {
    setFormKey(null);
    onClose();
  };
  const save = useMutation({
    mutationFn: async () => {
      const dates = {
        startsAt: new Date(form.startsAt!).toISOString(),
        endsAt: new Date(form.endsAt!).toISOString(),
      };
      const common = {
        name: form.name ?? '',
        ...dates,
        paused,
        link: form.link?.trim() ? form.link.trim() : null,
      };
      let saved = existing
        ? await api<CampaignDto>(`/admin/campaigns/${existing.id}`, {
            method: 'PATCH',
            body: common,
          })
        : await api<CampaignDto>('/admin/campaigns', {
            body: { ...common, brandId: form.brandId, regionId: form.regionId },
          });
      if (banner)
        saved = await api<CampaignDto>(`/admin/campaigns/${saved.id}/banner`, {
          method: 'PUT',
          body: { image: banner },
        });
      else if (removeBanner && saved.bannerUrl)
        saved = await api<CampaignDto>(`/admin/campaigns/${saved.id}/banner`, { method: 'DELETE' });
      return saved;
    },
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: campaignKeys.admin });
      void qc.invalidateQueries({ queryKey: ['sponsored-cells'] });
      void qc.invalidateQueries({ queryKey: ['home-banner'] });
      toast({ kind: 'success', title: tr('{name} saved', { name: c.name }) });
      close();
      if (!existing) onCreated(c);
    },
    onError: (e) =>
      toast({
        kind: 'error',
        title: e instanceof ApiError ? e.message : tr('Could not save the campaign'),
      }),
  });
  const pickable = brands.data?.filter((b) => !b.archived || b.id === existing?.brand.id) ?? [];
  const bannerSrc = banner ?? (removeBanner ? null : (existing?.bannerUrl ?? null));
  const valid =
    (form.name ?? '').trim().length >= 2 &&
    !!form.brandId &&
    !!form.startsAt &&
    !!form.endsAt &&
    form.endsAt > form.startsAt;
  return (
    <Sheet open={!!campaign} onClose={close} label={t('Campaign')}>
      {campaign ? (
        <div className="space-y-3" data-testid="campaign-sheet">
          <h2 className="font-display text-2xl font-bold">
            {existing ? t('Edit {name}', { name: existing.name }) : t('New campaign')}
          </h2>
          <label className="block text-sm font-semibold">
            {t('Brand')}
            <select
              value={form.brandId}
              disabled={!!existing}
              onChange={(e) => setForm({ ...form, brandId: e.target.value })}
              className={input}
              data-testid="campaign-brand"
            >
              <option value="">{t('Choose a brand…')}</option>
              {pickable.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
          {brands.data && !pickable.length ? (
            <p className="text-xs text-ink-400">{t('Add brands in the Brands tab first.')}</p>
          ) : null}
          <label className="block text-sm font-semibold">
            {t('Name')}
            <input
              value={form.name ?? ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={input}
              data-testid="campaign-name"
            />
          </label>
          <label className="block text-sm font-semibold">
            {t('City')}
            <select
              value={form.regionId}
              disabled={!!existing}
              onChange={(e) => setForm({ ...form, regionId: e.target.value })}
              className={input}
              data-testid="campaign-city"
            >
              {regions.data?.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm font-semibold">
              {t('Starts')}
              <input
                type="datetime-local"
                value={form.startsAt ?? ''}
                onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
                className={input}
                data-testid="campaign-starts"
              />
            </label>
            <label className="block text-sm font-semibold">
              {t('Ends')}
              <input
                type="datetime-local"
                value={form.endsAt ?? ''}
                onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
                className={input}
                data-testid="campaign-ends"
              />
            </label>
          </div>
          <label className="block text-sm font-semibold">
            {t('Link (blank = brand website)')}
            <input
              type="url"
              value={form.link ?? ''}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
              className={input}
              data-testid="campaign-link"
            />
          </label>
          <div>
            <div className="text-sm font-semibold">{t('Home banner (3:1, e.g. 1200×400)')}</div>
            {bannerSrc ? (
              <img
                src={bannerSrc}
                alt=""
                className="mt-1 w-full rounded-xl border border-ink-700 bg-ink-850"
              />
            ) : null}
            <div className="mt-1 flex gap-4 text-sm font-semibold">
              <label className="cursor-pointer text-volt">
                {t(bannerSrc ? 'Change banner' : 'Upload banner')}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="campaign-banner-input"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (f) setBanner(await fittedLogo(f, 1200, 400, 0.85).catch(() => null));
                  }}
                />
              </label>
              {bannerSrc ? (
                <button
                  className="text-ink-400"
                  onClick={() => (setBanner(null), setRemoveBanner(true))}
                >
                  {t('Remove')}
                </button>
              ) : null}
            </div>
          </div>
          {existing ? (
            <label className="flex items-center gap-3 text-sm font-semibold">
              <input
                type="checkbox"
                checked={paused}
                onChange={(e) => setPaused(e.target.checked)}
                className="size-5"
                data-testid="campaign-paused"
              />
              {t('Paused (hidden, no bonus XP)')}
            </label>
          ) : null}
          <Button
            className="w-full"
            loading={save.isPending}
            disabled={!valid}
            onClick={() => save.mutate()}
            data-testid="campaign-save"
          >
            {t(existing ? 'Save' : 'Save, then pick map cells')}
          </Button>
        </div>
      ) : null}
    </Sheet>
  );
}

/** Full-screen map of the campaign's city: tap cells to add or remove them. */
function CellPicker({
  campaign,
  all,
  onClose,
}: {
  campaign: CampaignDto;
  all: CampaignDto[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const t = useT();
  const region = useLoadedRegion(campaign.regionId);
  const [cells, setCells] = useState<string[]>(campaign.cells);
  // Cells other campaigns hold at an overlapping time can't be picked (shown in red).
  const taken = useMemo(() => {
    const s = new Set<string>();
    for (const c of all) {
      if (
        c.id === campaign.id ||
        c.regionId !== campaign.regionId ||
        c.startsAt >= campaign.endsAt ||
        c.endsAt <= campaign.startsAt
      )
        continue;
      for (const k of c.cells) s.add(k);
    }
    return s;
  }, [all, campaign]);
  const sponsored = useMemo(
    () =>
      cells.map((gridKey) => ({
        gridKey,
        color: campaign.brand.color,
        brandId: campaign.brand.id,
        logoUrl: campaign.brand.logoUrl,
      })),
    [cells, campaign],
  );
  const takenList = useMemo(() => [...taken], [taken]);
  const initialCenter = useMemo(() => {
    if (!region || !campaign.cells.length) return null;
    const pts = campaign.cells.map((k) => region.grid.centerOf(k));
    return {
      lat: pts.reduce((a, p) => a + p.lat, 0) / pts.length,
      lng: pts.reduce((a, p) => a + p.lng, 0) / pts.length,
    };
  }, [region, campaign.cells]);
  const save = useMutation({
    mutationFn: () =>
      api<CampaignDto>(`/admin/campaigns/${campaign.id}/cells`, {
        method: 'PUT',
        body: { gridKeys: cells },
      }),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: campaignKeys.admin });
      void qc.invalidateQueries({ queryKey: ['sponsored-cells'] });
      toast({ kind: 'success', title: tr('{n} cells saved for {name}', { n: c.cells.length, name: c.name }) });
      onClose();
    },
    onError: (e) =>
      toast({
        kind: 'error',
        title: e instanceof ApiError ? e.message : tr('Could not save the cells'),
      }),
  });
  const toggle = (key: string | null) => {
    if (!key) return;
    if (taken.has(key)) {
      toast({ kind: 'info', title: t('Another campaign sponsors this cell at that time') });
      return;
    }
    setCells((cur) =>
      cur.includes(key)
        ? cur.filter((k) => k !== key)
        : cur.length >= CAMPAIGN_MAX_CELLS
          ? cur
          : [...cur, key],
    );
  };
  return (
    <div className="fixed inset-0 z-50 bg-ink-950" data-testid="campaign-cell-picker">
      {region ? (
        <GameMap
          className="absolute inset-0"
          region={region}
          owners={NO_OWNERS}
          myUserId={null}
          sponsored={sponsored}
          lost={takenList}
          onSelect={toggle}
          initialZoom={campaign.cells.length ? 15 : 13.5}
          initialCenter={initialCenter}
        />
      ) : (
        <LoadingState />
      )}
      <div className="absolute inset-x-0 top-0 p-3 pt-[calc(var(--safe-top)+12px)]">
        <div className="mx-auto flex max-w-lg items-center gap-2 rounded-2xl border border-ink-700 bg-ink-900/90 px-4 py-2.5 backdrop-blur">
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold">{campaign.name}</div>
            <div className="text-xs text-ink-400" data-testid="campaign-cell-count">
              {t(cells.length === 1 ? '{n} cell · tap to add / remove' : '{n} cells · tap to add / remove', { n: cells.length })}
            </div>
          </div>
          <Button size="sm" variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            size="sm"
            loading={save.isPending}
            onClick={() => save.mutate()}
            data-testid="campaign-cells-save"
          >
            {t('Save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
